import React, { useMemo, useState } from "react";
import { Check, Loader2, Receipt } from "lucide-react";
import {
  FilaPorcaoDescricao,
  FilaPorcaoItem,
} from "../../../services/filaPorcoesService";
import { resolveSalaoProducaoLabels } from "../../Painel/utils/origemPainel";
import SalaoProducaoOrderHeader from "../../../components/SalaoProducaoOrderHeader";
import PedidoOriginalModal from "../../../components/PedidoOriginalModal";

interface PorcaoItemCardProps {
  item: FilaPorcaoItem;
  actionId: number | null;
  onFinalizar: (id: number) => void;
}

const resolveDescricao = (item: FilaPorcaoItem): FilaPorcaoDescricao | null => {
  if (Array.isArray(item.descricao) && item.descricao.length > 0) {
    return item.descricao[0];
  }
  return null;
};

const PorcaoItemCard: React.FC<PorcaoItemCardProps> = ({
  item,
  actionId,
  onFinalizar,
}) => {
  const [showPedidoModal, setShowPedidoModal] = useState(false);

  const desc = resolveDescricao(item);
  const nome = desc?.name || item.produtoNome;
  const adicionais = desc?.adicionais?.length
    ? desc.adicionais
    : Array.isArray(item.adicionais)
      ? item.adicionais.map((a) =>
          a.quantidade && a.quantidade > 1
            ? `${a.nome} x${a.quantidade}`
            : a.nome
        )
      : [];
  const observacao = desc?.observacao || item.observacao || null;
  const salaoLabels = resolveSalaoProducaoLabels({
    mesaId: item.pedidoFoodfly?.mesaId,
    comandaId: item.pedidoFoodfly?.comandaId,
    identificador: item.pedidoFoodfly?.identificador,
    displayId: item.numeroPedido,
  });
  const pedidoOriginalOrderId = useMemo(() => {
    const n = parseInt(String(item.foodflyPedidoId || ""), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [item.foodflyPedidoId]);
  const isBusy = actionId === item.id;
  const isDone = item.status === "done";
  const canFinalizar =
    item.status === "pending" || item.status === "in_progress";

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm transition-shadow hover:shadow-md">
      <div className="mb-3">
        <SalaoProducaoOrderHeader
          labels={salaoLabels}
          layout="stacked"
          fallbackBadge={
            salaoLabels.mesa || salaoLabels.comandaLabel
              ? undefined
              : item.numeroPedido != null
                ? `Pedido #${item.numeroPedido}`
                : undefined
          }
        />
      </div>

      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="mb-2 flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Porção
          </span>
          <span className="text-sm font-bold leading-5 text-slate-800">
            {nome}
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
            Adicionais
          </span>
          {adicionais.length > 0 ? (
            <div className="space-y-0.5">
              {adicionais.map((a, idx) => (
                <p
                  key={`${item.id}-ad-${idx}`}
                  className="text-sm font-semibold leading-5 text-slate-700"
                >
                  {a}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-sm font-medium text-slate-500">Sem adicionais</p>
          )}
        </div>

        {observacao ? (
          <>
            <div className="my-2 border-t border-slate-300" />
            <p className="rounded-md border border-error-dark bg-error-light px-2.5 py-1.5 text-xs font-bold text-error-text shadow-sm">
              {observacao}
            </p>
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        {pedidoOriginalOrderId != null ? (
          <button
            type="button"
            onClick={() => setShowPedidoModal(true)}
            className="flex min-h-[44px] items-center justify-center gap-1.5 rounded-md border border-violet-600 bg-violet-600 px-3 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-violet-700 active:bg-violet-800 sm:min-h-0 sm:py-2 sm:text-xs"
            title="Ver recibo original"
            aria-label="Ver recibo original do pedido"
          >
            <Receipt size={15} strokeWidth={2.5} />
            Recibo
          </button>
        ) : null}
        {!isDone && canFinalizar ? (
          <button
            type="button"
            onClick={() => onFinalizar(item.id)}
            disabled={isBusy}
            className="flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-md border border-emerald-600 bg-emerald-600 px-3 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0 sm:py-2 sm:text-xs"
          >
            {isBusy ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <Check size={15} strokeWidth={2.5} />
            )}
            Finalizar
          </button>
        ) : null}
      </div>

      {pedidoOriginalOrderId != null ? (
        <PedidoOriginalModal
          orderId={pedidoOriginalOrderId}
          isOpen={showPedidoModal}
          onClose={() => setShowPedidoModal(false)}
        />
      ) : null}
    </div>
  );
};

export default PorcaoItemCard;
